// ============================================================
// Offline-Erfassung (SPEC §15.3): Mängel und Fotos, die ohne Netz erfasst werden, liegen
// in IndexedDB und gehen raus, sobald wieder Netz da ist. Bewusst ohne Background Sync:
// das kann Safari auf dem iPad nicht. Übertragen wird beim Start, beim Ereignis „online"
// und minütlich, solange etwas wartet.
//
// Grenzen: nur Neuanlagen (kein Ändern offline, sonst bräuchte es Konfliktlösung), und
// ein Eintrag, dessen Antwort im Funkloch verloren ging, kann doppelt ankommen.
//
// Jeder Eintrag gehört der Person, die ihn erfasst hat (`nutzer`), und geht nur in deren
// Sitzung hinaus — auf einem geteilten Baustellen-Tablet lüde ihn sonst die nächste
// Person unter ihrem Namen hoch (Durchsicht 27.10.2026).
// ============================================================

import { ApiFehler, schick } from "../api/client";

export type Vorgemerkt =
  | { id: string; nutzer?: string; art: "mangel"; projektId: string; titel: string; rumpf: Record<string, unknown>; bilder: Blob[]; namen: string[]; am: string }
  | { id: string; nutzer?: string; art: "foto"; projektId: string; titel: string; ziel: string; zielId: string; bilder: Blob[]; namen: string[]; am: string };

type Neu = Omit<Extract<Vorgemerkt, { art: "mangel" }>, "id" | "am" | "nutzer"> | Omit<Extract<Vorgemerkt, { art: "foto" }>, "id" | "am" | "nutzer">;

/** Wer gerade angemeldet ist (App setzt es beim Rendern, vor den Effekten der Kopfleiste). */
let besitzer: string | null = null;
export function setzeBesitzer(id: string | null): void {
  besitzer = id;
}

/** Einträge ohne `nutzer` stammen von vor der Bindung; sie zählen für jeden, damit sie
 *  nicht unsichtbar liegen bleiben. */
const meins = (e: Vorgemerkt) => !e.nutzer || e.nutzer === besitzer;

const DB = "lilasp-bauleitung";
const STORE = "warteschlange";
const EREIGNIS = "warteschlange";

function oeffnen(): Promise<IDBDatabase> {
  return new Promise((ok, fehler) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "id" });
    r.onsuccess = () => ok(r.result);
    r.onerror = () => fehler(r.error);
  });
}

async function tun<T>(modus: IDBTransactionMode, f: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await oeffnen();
  return new Promise((ok, fehler) => {
    const t = db.transaction(STORE, modus);
    const r = f(t.objectStore(STORE));
    t.oncomplete = () => { ok(r.result); db.close(); };
    t.onerror = () => { fehler(t.error); db.close(); };
  });
}

const melden = () => window.dispatchEvent(new Event(EREIGNIS));

export async function vormerken(e: Neu): Promise<void> {
  await tun("readwrite", (s) => s.put({ ...e, nutzer: besitzer ?? undefined, id: crypto.randomUUID(), am: new Date().toISOString() }));
  melden();
}

/** Was die angemeldete Person vorgemerkt hat. */
export async function vorgemerkt(): Promise<Vorgemerkt[]> {
  if (!besitzer) return [];
  try {
    return ((await tun("readonly", (s) => s.getAll())) as Vorgemerkt[]).filter(meins).sort((a, b) => a.am.localeCompare(b.am));
  } catch {
    return [];                               // privater Modus ohne IndexedDB: nichts gemerkt
  }
}

/** Beim Abmelden auf Wunsch: alles der angemeldeten Person verwerfen. */
export async function alleVerwerfen(): Promise<void> {
  for (const e of await vorgemerkt()) await tun("readwrite", (s) => s.delete(e.id));
  melden();
}

export async function verwerfen(id: string): Promise<void> {
  await tun("readwrite", (s) => s.delete(id));
  melden();
}

export function beobachten(f: () => void): () => void {
  window.addEventListener(EREIGNIS, f);
  return () => window.removeEventListener(EREIGNIS, f);
}

/** Ist der Fehler „kein Netz" (und nicht etwa 422)? Dann lohnt das Vormerken. */
export function ohneNetz(f: unknown): boolean {
  return !navigator.onLine || (f instanceof ApiFehler && f.status === 0);
}

function formular(bilder: Blob[], namen: string[], ziel: string, zielId: string): FormData {
  const d = new FormData();
  bilder.forEach((b, i) => d.append("dateien", b, namen[i] ?? `foto-${i + 1}.jpg`));
  d.append("ziel", ziel);
  d.append("ziel_id", zielId);
  return d;
}

let laeuft = false;

/** Überträgt, was wartet. Gibt die Fehler zurück, die nicht am Netz lagen (z. B. 403,
 *  weil das Projekt inzwischen nicht mehr zugänglich ist) — die bleiben stehen und werden
 *  angezeigt, statt still verloren zu gehen. */
export async function abarbeiten(): Promise<{ uebertragen: number; fehler: { e: Vorgemerkt; text: string }[] }> {
  if (laeuft || !navigator.onLine) return { uebertragen: 0, fehler: [] };
  laeuft = true;
  let uebertragen = 0;
  const fehler: { e: Vorgemerkt; text: string }[] = [];
  try {
    for (const e of await vorgemerkt()) {
      try {
        if (e.art === "mangel") {
          const m = await schick<{ id: string; nummer?: string }>(`/projekte/${e.projektId}/maengel`, e.rumpf);
          if (e.bilder.length) {
            // Der Mangel steht. Scheitern jetzt die Fotos, wartet NUR noch ihr Hochladen —
            // sonst legte jeder neue Versuch denselben Mangel noch einmal an.
            const rest: Vorgemerkt = { id: e.id, nutzer: e.nutzer, art: "foto", projektId: e.projektId,
                                       titel: `Fotos zu ${m.nummer ?? e.titel}`, ziel: "mangel_id", zielId: m.id,
                                       bilder: e.bilder, namen: e.namen, am: e.am };
            await tun("readwrite", (s) => s.put(rest));
            await schick(`/projekte/${e.projektId}/fotos`, formular(e.bilder, e.namen, "mangel_id", m.id));
          }
        } else {
          await schick(`/projekte/${e.projektId}/fotos`, formular(e.bilder, e.namen, e.ziel, e.zielId));
        }
        await verwerfen(e.id);
        uebertragen++;
      } catch (f) {
        if (ohneNetz(f)) break;              // wieder Funkloch: später weiter
        fehler.push({ e, text: f instanceof Error ? f.message : String(f) });
      }
    }
  } finally {
    laeuft = false;
  }
  return { uebertragen, fehler };
}
